import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";

//Import types, apparently not needed to lazy load as it is only types
import type { QRCodeToDataURLOptions, QRCodeToStringOptions } from "qrcode";

export enum QrCodeExportFormat {
  png,
  jpg,
  webp,
  svg,
  utf8,
}

export interface QrCodeGeneratorState {
  targetFormat: QrCodeExportFormat;
  embeddedData: string;
  qrCodeWidthPx: number;
  lightColor: string;
  darkColor: string;
}

const initialState: QrCodeGeneratorState = {
  targetFormat: QrCodeExportFormat.png,
  embeddedData: "https://numberchomper.com",
  qrCodeWidthPx: 512,
  lightColor: "#FFFFFF",
  darkColor: "#000000",
};

export const QrCodeGeneratorSlice = createSlice({
  name: "qrCodeGenerator",
  initialState,
  reducers: {
    embeddedDataChanged: (state, action: PayloadAction<string>) => {
      state.embeddedData = action.payload;
    },
    targetFormatChanged: (state, action: PayloadAction<QrCodeExportFormat>) => {
      state.targetFormat = action.payload;
    },
    qrCodeWidthChanged: (state, action: PayloadAction<number>) => {
      state.qrCodeWidthPx = action.payload;
    },
    lightColorChanged: (state, action: PayloadAction<string>) => {
      state.lightColor = action.payload;
    },
    darkColorChanged: (state, action: PayloadAction<string>) => {
      state.darkColor = action.payload;
    },
  },
});

export const generateQrCodeAsync = createAsyncThunk<
  void,
  void,
  { state: RootState }
>("qrCodeGenerator/generateQrCodeAsync", async (_, { getState }) => {
  const state = getState();

  //Lazy load qrcode library
  const qrCodetModule = await import("qrcode");
  var qrCode = qrCodetModule.default;

  var selectedFormat = QrCodeExportFormat[state.qrCodeGenerator.targetFormat];

  if (
    state.qrCodeGenerator.targetFormat == QrCodeExportFormat.jpg ||
    state.qrCodeGenerator.targetFormat == QrCodeExportFormat.png ||
    state.qrCodeGenerator.targetFormat == QrCodeExportFormat.webp
  ) {
    var options: QRCodeToDataURLOptions = {
      errorCorrectionLevel: "H",
      type:
        state.qrCodeGenerator.targetFormat === QrCodeExportFormat.webp
          ? "image/webp"
          : state.qrCodeGenerator.targetFormat === QrCodeExportFormat.jpg
          ? "image/jpeg"
          : "image/png",
      width: state.qrCodeGenerator.qrCodeWidthPx,
      margin: 1,
      color: {
        dark: state.qrCodeGenerator.darkColor,
        light: state.qrCodeGenerator.lightColor,
      },
    };

    var dataUrl = await qrCode.toDataURL(
      state.qrCodeGenerator.embeddedData,
      options
    );

    const a = document.createElement("a");
    document.body.appendChild(a);
    a.href = dataUrl;
    a.download = `QrCode.${selectedFormat}`;
    a.click();

    // Clean up
    URL.revokeObjectURL(dataUrl);
    a.remove();
  } else if (
    state.qrCodeGenerator.targetFormat == QrCodeExportFormat.svg ||
    state.qrCodeGenerator.targetFormat == QrCodeExportFormat.utf8
  ) {
    var optionsToString: QRCodeToStringOptions = {
      type:
        state.qrCodeGenerator.targetFormat == QrCodeExportFormat.svg
          ? "svg"
          : "utf8",
      color: {
        dark: state.qrCodeGenerator.darkColor,
        light: state.qrCodeGenerator.lightColor,
      },
    };

    var qrString = await qrCode.toString(
      state.qrCodeGenerator.embeddedData,
      optionsToString
    );

    const blob = new Blob([qrString], {
      type:
        state.qrCodeGenerator.targetFormat == QrCodeExportFormat.svg
          ? "image/svg+xml"
          : "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    document.body.appendChild(a);
    a.href = url;
    a.download = `QrCode.${
      state.qrCodeGenerator.targetFormat == QrCodeExportFormat.svg
        ? "svg"
        : "txt"
    }`;
    a.click();

    // Clean up
    URL.revokeObjectURL(url);
    a.remove();
  }
});

export const {
  embeddedDataChanged,
  targetFormatChanged,
  qrCodeWidthChanged,
  lightColorChanged,
  darkColorChanged,
} = QrCodeGeneratorSlice.actions;

export const selectState = (state: RootState) => state.qrCodeGenerator;

export default QrCodeGeneratorSlice.reducer;
